import React, { useCallback, useMemo } from 'react';
import { type WrappedComponentProps, injectIntl } from 'react-intl-next';
import { SearchFooter } from '@atlassian/search-dialog';
import { isFedrampModerate, isIsolatedCloud } from '@atlaskit/atlassian-context';
import { fg } from '@atlaskit/platform-feature-flags';

import { AdvancedSearch } from '../../advanced-search/advanced-search';
import { SearchDialogProduct, useProductContext } from '../../product-router';
import { ProductStates } from '../../product-state-machine/product-state-machine-types';
import { messages } from './messages';
import { getSections } from './sections';

import { COMPASS_PRODUCT_ID, type CompassTabProps, useCompassSuppliers } from './suppliers';
import { CompassScope, type CompassURLGenerators } from './types';
import { compassURLGenerators } from './url-generators';
import { useUserContext } from '../../../common/user-context';
import { useShowAtlassianHomeSearchLink } from '../../../common/show-atlassian-home-search-link';
import {
	AtlassianHomeSearchLink,
	getAtlassianHomeSearchUrl,
} from '../../advanced-search-footer/atlassian-home-search-link';
import { type AdvancedSearchUrlGenerator } from '../../product-router/product-router-state';
import { useTypedAggregatorClient } from '../../aggregator-client-context';

const UpsellModal = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_product-search-dialog/async-chunk/compass-upsell-modal" */ './upsell-tab/upsell-modal'
	).then((mod) => ({ default: mod.UpsellModal })),
);

/**
 * Callback function to be used by PSD's permission supplier.
 * @returns all Compass scope required for Compass tab to function properly.
 */
const upsellPermissionSupplier = () => {
	const response = Object.values(CompassScope);

	return Promise.resolve(response);
};

const CompassTabWithIntl = ({
	baseUrl,
	crossFlowEnabled,
	intl,
	isCompassActivated = false,
	isUserAnonymous = false,
	postQueryItemSupplier,
	preQueryItemSupplier,
	sourceContext,
	upsellCtaButtonProps,
	userTeamConfig,
	...rest
}: WrappedComponentProps & CompassTabProps) => {
	const { advancedSearchLinkGenerator }: CompassURLGenerators = useMemo(() => {
		return compassURLGenerators(baseUrl);
	}, [baseUrl]);
	const sections = getSections(intl);

	const {
		preQueryItemSupplier: preQueryItemSupplierWithPeopleAndTeams,
		postQueryItemSupplier: postQueryItemSupplierWithPeopleAndTeams,
		preQuerySectionTitleGenerator,
	} = useCompassSuppliers(
		{ userTeamConfig, preQueryItemSupplier, postQueryItemSupplier },
		sections,
	);

	const getAdvancedSearchButtonLabel = useCallback(
		(productState: ProductStates) => {
			switch (productState) {
				case ProductStates.PreQueryLoading:
				case ProductStates.PreQuerySuccess:
				case ProductStates.PreQueryError:
				case ProductStates.PreQueryNoResult:
					return intl.formatMessage(messages.compass_view_all_components);
				default:
					return intl.formatMessage(messages.compass_view_all_results);
			}
		},
		[intl],
	);

	// https://switcheroo.atlassian.com/ui/gates/87dfd547-5dae-40da-ab90-67c266e10fbd/key/compass-psd-hide-upsell-ad
	const shouldHideUpsellAd = fg('compass-psd-hide-upsell-ad');

	const shouldUpsell =
		!shouldHideUpsellAd &&
		!isUserAnonymous &&
		crossFlowEnabled &&
		!isCompassActivated &&
		(fg('hide_fedramp_compass_search_tab') ? !isFedrampModerate() : true) &&
		(fg('hide_isolated_cloud_compass_search_tab') ? !isIsolatedCloud() : true);

	const permissionSupplier = shouldUpsell ? upsellPermissionSupplier : rest.permissionSupplier;

	const { orgId } = useUserContext();
	const { getPrimaryProduct } = useProductContext();
	const primaryProduct = getPrimaryProduct();
	const aggregatorClient = useTypedAggregatorClient();
	const cloudId = aggregatorClient?.getAggregatorConfig().cloudId;
	const showAtlassianHomeSearchLink = !!(
		useShowAtlassianHomeSearchLink(primaryProduct?.id) &&
		orgId &&
		cloudId
	);

	const generateAdvancedSearchUrl: AdvancedSearchUrlGenerator = useCallback(
		(query, _, usage) => {
			if (usage === 'onInputEnterModified' && showAtlassianHomeSearchLink) {
				return getAtlassianHomeSearchUrl(orgId, cloudId, query, primaryProduct?.id);
			}
			return advancedSearchLinkGenerator(query);
		},
		[advancedSearchLinkGenerator, orgId, cloudId, showAtlassianHomeSearchLink, primaryProduct?.id],
	);

	return (
		<SearchDialogProduct
			id={COMPASS_PRODUCT_ID}
			sections={sections}
			title={intl.formatMessage(messages.compass_tab_label)}
			expandedStateInputPlaceholder={intl.formatMessage(
				messages.compass_expanded_input_placeholder,
			)}
			generateAdvancedSearchUrl={generateAdvancedSearchUrl}
			preQuerySectionTitleGenerator={preQuerySectionTitleGenerator}
			preQueryItemSupplier={preQueryItemSupplierWithPeopleAndTeams}
			postQueryItemSupplier={postQueryItemSupplierWithPeopleAndTeams}
			permissionSupplier={permissionSupplier}
			{...rest}
		>
			{({ linkComponent, productState, query }) => {
				if (shouldUpsell) {
					return {
						Body: () => (
							<UpsellModal
								sourceContext={sourceContext ?? 'product search dialog'}
								ctaButtonProps={upsellCtaButtonProps}
							/>
						),
						Footer: () => <></>,
					};
				}

				const advancedSearchUrl: string = advancedSearchLinkGenerator(query);
				const message = getAdvancedSearchButtonLabel(productState);

				return {
					Header: () => (
						<AdvancedSearch
							advancedSearchMessage={message}
							advancedSearchUrl={advancedSearchUrl}
							linkComponent={linkComponent}
						/>
					),
					Footer: () => (
						<>
							{showAtlassianHomeSearchLink && (
								<SearchFooter>
									<AtlassianHomeSearchLink orgId={orgId} cloudId={cloudId} showModifierIcon />
								</SearchFooter>
							)}
						</>
					),
				};
			}}
		</SearchDialogProduct>
	);
};

export const CompassTab = injectIntl(CompassTabWithIntl);
